// Auth0-compatible email validation — ports validator.js isEmail with default options
// https://github.com/validatorjs/validator.js/blob/master/src/lib/isEmail.js (MIT License)
// Default options: allow_utf8_local_part=true, require_tld=true, allow_ip_domain=false, allow_underscores=false

const MAX_EMAIL_LENGTH = 254;
const emailUserUtf8Part = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\u00A1-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
const quotedEmailUserUtf8 = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
const tldPattern = /^([a-z\u00A1-\u00A8\u00AA-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}|xn[a-z0-9-]{2,})$/i;
const fqdnPartPattern = /^[a-z_\u00a1-\uffff0-9-]+$/i;
const fullWidthChars = /[\uff01-\uff5e]/;
const numericOnly = /^\d+$/;

function isByteLength(str: string, options: { min?: number; max?: number }): boolean {
  const len = new TextEncoder().encode(str).length;
  if (options.min !== undefined && len < options.min) return false;
  if (options.max !== undefined && len > options.max) return false;
  return true;
}

function isFqdn(domain: string): boolean {
  const parts = domain.split('.');
  if (parts.length < 2) return false;

  const tld = parts[parts.length - 1];

  if (numericOnly.test(tld)) return false;
  if (!tldPattern.test(tld)) return false;
  if (/\s/.test(tld)) return false;

  for (var i = 0; i < parts.length; i++) {
    var part = parts[i];
    if (part.length > 63) return false;
    if (!fqdnPartPattern.test(part)) return false;
    if (fullWidthChars.test(part)) return false;
    if (/^-|-$/.test(part)) return false;
    if (/_/.test(part)) return false;
  }

  return true;
}

export function isValidAuth0Email(email: string): boolean {
  if (!email || email.length > MAX_EMAIL_LENGTH) return false;

  var atIndex = email.lastIndexOf('@');
  if (atIndex <= 0 || atIndex === email.length - 1) return false;

  var user = email.substring(0, atIndex);
  var domain = email.substring(atIndex + 1);

  if (!isFqdn(domain)) return false;

  if (!isByteLength(user, { max: 64 }) || !isByteLength(domain, { max: 254 })) return false;

  // Quoted local part
  if (user.length >= 2 && user[0] === '"' && user[user.length - 1] === '"') {
    var innerUser = user.substring(1, user.length - 1);
    return quotedEmailUserUtf8.test(innerUser);
  }

  // Standard local part
  var segments = user.split('.');
  for (var i = 0; i < segments.length; i++) {
    if (segments[i].length === 0 || !emailUserUtf8Part.test(segments[i])) return false;
  }

  return true;
}
